import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { MoreVertical } from 'lucide-react';
import { apiGet, apiPost } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';
import UiSelect from '../components/ui/Select';
import Modal from '../components/ui/Modal';

const MENU_ITEMS_CLS = "z-[60] mt-1.5 min-w-[170px] rounded-xl bg-surface border border-border-2 p-1 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.7)] focus:outline-none origin-top transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0";
const menuItemCls = (danger) => ['w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors',
  danger ? 'text-red data-[focus]:bg-[rgba(239,68,68,0.08)]' : 'text-ink-2 data-[focus]:bg-white/[0.05] data-[focus]:text-ink'].join(' ');

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const toArr = x => Array.isArray(x) ? x : (x?.items || []);
const ROLES = ['owner', 'accountant', 'branch_manager', 'sales'];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('all');
  const [revokeUser, setRevokeUser] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(toArr(await apiGet(ENDPOINTS.ADMIN.USERS, { limit: 500 }))); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suspend = async (u) => { setError(''); try { await apiPost(ENDPOINTS.ADMIN.USER_SUSPEND(u.id), {}); await load(); } catch (err) { setError(err.message); } };
  const activate = async (u) => { setError(''); try { await apiPost(ENDPOINTS.ADMIN.USER_ACTIVATE(u.id), {}); await load(); } catch (err) { setError(err.message); } };
  const confirmRevoke = async () => {
    if (!revokeUser) return;
    setBusy(true); setError('');
    try { await apiPost(ENDPOINTS.ADMIN.USER_REVOKE(revokeUser.id), {}); setRevokeUser(null); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (role !== 'all' && u.role !== role) return false;
      if (q) {
        const hay = [u.first_name, u.last_name, u.email, u.organization_name].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, role]);

  return (
    <div className="page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Users</strong></div>
        <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
          <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Users</em>
        </h1>
        <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}><strong style={{ color: '#FAFAFA' }}>{filtered.length}</strong> users across all organizations</p>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input className="field-input" placeholder="Search name, email, org…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
        <div style={{ width: 180 }}>
          <UiSelect value={role} onChange={setRole} options={[{ value: 'all', label: 'All roles' }, ...ROLES.map(r => ({ value: r, label: r.replace('_', ' ').replace(/^\w/, c => c.toUpperCase()) }))]} />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead><tr><th>Name</th><th>Email</th><th>Organization</th><th>Role</th><th>Status</th><th>Last login</th><th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#A1A1AA' }}>Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>No users found</td></tr>}
            {filtered.map(u => {
              const suspended = u.is_active === false;
              return (
                <tr key={u.id}>
                  <td style={{ color: '#FAFAFA' }}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '-'}</td>
                  <td style={{ color: '#A1A1AA', fontFamily: 'monospace', fontSize: 12 }}>{u.email}</td>
                  <td style={{ color: '#D4D4D8' }}>{u.organization_name || u.org_name || '-'}</td>
                  <td style={{ color: '#D4D4D8', textTransform: 'capitalize' }}>{(u.role || '').replace('_', ' ')}</td>
                  <td><StatusPill status={suspended ? 'on_hold' : 'active'} label={suspended ? 'Suspended' : 'Active'} /></td>
                  <td className="tbl-mono">{u.last_login ? fmtDate(u.last_login) : 'Never'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <Menu as="div" className="relative inline-block text-left">
                      <MenuButton onClick={e => e.stopPropagation()} className="mini-btn" title="Actions"><MoreVertical size={14} /></MenuButton>
                      <MenuItems anchor="bottom end" transition className={MENU_ITEMS_CLS}>
                        {suspended
                          ? <MenuItem><button onClick={() => activate(u)} className={menuItemCls(false)}>Reactivate user</button></MenuItem>
                          : <MenuItem><button onClick={() => suspend(u)} className={menuItemCls(true)}>Suspend user</button></MenuItem>}
                        <MenuItem><button onClick={() => setRevokeUser(u)} className={menuItemCls(true)}>Revoke all sessions</button></MenuItem>
                      </MenuItems>
                    </Menu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={!!revokeUser} onClose={() => !busy && setRevokeUser(null)} title="Revoke all sessions" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: '#A1A1AA', lineHeight: 1.5 }}>
            Sign <strong style={{ color: '#FAFAFA' }}>{revokeUser ? ([revokeUser.first_name, revokeUser.last_name].filter(Boolean).join(' ') || revokeUser.email) : ''}</strong> out of every device immediately by invalidating all of their access and refresh tokens. They'll need to log in again. This does not suspend the account.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-ghost" onClick={() => setRevokeUser(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={confirmRevoke} disabled={busy}>{busy ? 'Revoking…' : 'Revoke sessions'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
